import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword, deleteUser } from 'firebase/auth';
import { doc, setDoc, collectionGroup, query, where, getDocs, limit, collection } from 'firebase/firestore';
import { auth, db } from '../firebase';
import './Signup.css';

function Signup({ onSignupSuccess }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    studentId: '',
    role: 'parent',
    teacherType: 'regular',
    specialtySubjects: [],
    selectedSchool: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [allSchools, setAllSchools] = useState([]);

  // Load schools from Firestore so we use the real UUID document IDs
  // (not school names) as the schoolId value on signup.
  useEffect(() => {
    const loadSchools = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'schools'));
        const schoolsData = snapshot.docs.map(d => ({
          id: d.id,            // Firestore document ID (UUID string)
          name: d.data().name,
          type: d.data().type,
          schoolCode: d.data().schoolCode || ''
        }));
        setAllSchools(schoolsData);
      } catch (err) {
        console.error('Signup: Failed to load schools from Firestore:', err);
      }
    };
    loadSchools();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'studentId') {
      // Allow alphanumeric + hyphen for new format (e.g., AB12345-2026) or old 6-char format
      const cleanedValue = value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
      setFormData({
        ...formData,
        [name]: cleanedValue
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (formData.studentId) {
      const sid = formData.studentId;
      // Legacy: 6-char alphanumeric (e.g., AB1234)
      const isValidLegacy = /^[A-Z0-9]{6}$/.test(sid);
      // Simple new format: 6 chars + "-YYYY" (e.g., AB1234-2026)
      const isValidSimpleNew = /^[A-Z0-9]{6}-\d{4}$/.test(sid);
      // Full new format: [SCHOOL_CODE]-[6-char code]-[YEAR] (e.g., SPS-ET7HJR-2026)
      const isValidFullNew = /^[A-Z]{3}-[A-Z0-9]{6}-\d{4}$/.test(sid);
      if (!isValidLegacy && !isValidSimpleNew && !isValidFullNew) {
        setError('Student ID must be 6 characters (e.g., AB1234), simple format (e.g., AB1234-2026), or full format (e.g., SPS-ET7HJR-2026)');
        return;
      }
    }

    if (formData.role === 'student' && !formData.studentId) {
      setError('Student ID is required for student accounts');
      return;
    }

    if (formData.role === 'teacher' && formData.teacherType === 'subject' && formData.specialtySubjects.length === 0) {
      setError('At least one specialty subject is required for subject teachers');
      return;
    }

    setLoading(true);
    let userCredential = null;

    try {
      userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      if (formData.role === 'parent') {
        const parentData = {
          id: userCredential.user.uid,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phoneNumber: formData.phoneNumber || null,
          linkedChildrenIds: [],
          createdAt: new Date().toISOString(),
          isActive: true
        };

        // Link child if student ID provided - search in class subcollections
        let parentSchoolId = null;
        if (formData.studentId) {
          const studentsQuery = query(
            collectionGroup(db, 'students'),
            where('studentId', '==', formData.studentId),
            limit(1)
          );
          const studentsSnapshot = await getDocs(studentsQuery);

          if (studentsSnapshot.empty) {
            await deleteUser(userCredential.user);
            setError('Student ID not found. Please verify with your child\'s teacher.');
            setLoading(false);
            return;
          }

          parentData.linkedChildrenIds = [formData.studentId];

          // Inherit schoolId from linked student if found
          if (!studentsSnapshot.empty) {
            const studentDoc = studentsSnapshot.docs[0];
            parentSchoolId = studentDoc.data().schoolId || null;
          }
        }

        await setDoc(doc(db, 'parents', userCredential.user.uid), { ...parentData, schoolId: parentSchoolId });
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          id: userCredential.user.uid,
          username: `${formData.firstName} ${formData.lastName}`,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          role: 'Parent',
          assignedClassIds: [],
          schoolId: parentSchoolId,
          createdAt: new Date().toISOString()
        });
      } else if (formData.role === 'teacher') {
        // Teacher signup
        const teacherData = {
          id: userCredential.user.uid,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phoneNumber: formData.phoneNumber || null,
          teacherType: formData.teacherType,
          specialtySubjects: formData.teacherType === 'subject' ? formData.specialtySubjects : [],
          createdAt: new Date().toISOString(),
          isActive: true
        };

        await setDoc(doc(db, 'teachers', userCredential.user.uid), { ...teacherData, schoolId: formData.selectedSchool || null });
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          id: userCredential.user.uid,
          uuid: crypto.randomUUID(),
          username: `${formData.firstName} ${formData.lastName}`,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          role: 'Teacher',
          teacherType: formData.teacherType,
          specialtySubjects: formData.teacherType === 'subject' ? formData.specialtySubjects : [],
          assignedClassIds: [],
          schoolId: formData.selectedSchool || null,
          createdAt: new Date().toISOString()
        });
        console.log('✅ Teacher user document saved:', userCredential.user.uid);
      } else if (formData.role === 'student') {
        // Student signup - verify student ID exists
        const studentsQuery = query(
          collectionGroup(db, 'students'),
          where('studentId', '==', formData.studentId),
          limit(1)
        );
        const studentsSnapshot = await getDocs(studentsQuery);

        if (studentsSnapshot.empty) {
          await deleteUser(userCredential.user);
          setError('Student ID not found in school records. Please contact your school administrator.');
          setLoading(false);
          return;
        }

        const studentDoc = studentsSnapshot.docs[0];
        const studentData = studentDoc.data();
        const studentSchoolId = studentData.schoolId || null;
        const classId = studentDoc.ref.parent.parent.id; // Get the class ID from the parent path

        // Update the existing student record with auth info
        await setDoc(doc(db, 'classes', classId, 'students', studentDoc.id), {
          ...studentData,
          userId: userCredential.user.uid,
          email: formData.email,
          phoneNumber: formData.phoneNumber || null
        }, { merge: true });

        // Link the top-level students lookup record to the auth account so the
        // student dashboard can resolve it.
        await setDoc(doc(db, 'students', formData.studentId), {
          studentId: formData.studentId,
          schoolId: studentSchoolId,
          classId,
          userId: userCredential.user.uid,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          updatedAt: new Date().toISOString()
        }, { merge: true });
        
        console.log('✅ Student record updated with email:', formData.email);

        await setDoc(doc(db, 'users', userCredential.user.uid), {
          id: userCredential.user.uid,
          uuid: crypto.randomUUID(),
          username: `${formData.firstName} ${formData.lastName}`,
          firstName: formData.firstName,
          lastName: formData.lastName,
          studentId: formData.studentId,
          email: formData.email,
          role: 'Student',
          assignedClassIds: [classId],
          schoolId: studentSchoolId,
          createdAt: new Date().toISOString()
        });
        console.log('✅ Student account created:', userCredential.user.uid);
      } else if (formData.role === 'superAdmin') {
        // Super Admin signup
        const superAdminData = {
          id: userCredential.user.uid,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phoneNumber: formData.phoneNumber || null,
          createdAt: new Date().toISOString(),
          isActive: true
        };

        await setDoc(doc(db, 'superAdmins', userCredential.user.uid), superAdminData);
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          id: userCredential.user.uid,
          uuid: crypto.randomUUID(),
          username: `${formData.firstName} ${formData.lastName}`,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          role: 'Super Admin',
          assignedClassIds: [],
          schoolId: null,
          createdAt: new Date().toISOString()
        });
        console.log('✅ Super Admin user document saved:', userCredential.user.uid);
      } else if (formData.role === 'scanner') {
        // Scanner signup
        const scannerData = {
          id: userCredential.user.uid,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phoneNumber: formData.phoneNumber || null,
          createdAt: new Date().toISOString(),
          isActive: true
        };

        await setDoc(doc(db, 'scanners', userCredential.user.uid), scannerData);
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          id: userCredential.user.uid,
          uuid: crypto.randomUUID(),
          username: `${formData.firstName} ${formData.lastName}`,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          role: 'Scanner',
          assignedClassIds: [],
          schoolId: formData.selectedSchool || null,
          createdAt: new Date().toISOString()
        });
        console.log('✅ Scanner user document saved:', userCredential.user.uid);
      } else {
        // Admin signup
        const adminData = {
          id: userCredential.user.uid,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phoneNumber: formData.phoneNumber || null,
          createdAt: new Date().toISOString(),
          isActive: true
        };

        await setDoc(doc(db, 'admins', userCredential.user.uid), adminData);
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          id: userCredential.user.uid,
          uuid: crypto.randomUUID(), // Generate UUID for internal references like iOS
          username: `${formData.firstName} ${formData.lastName}`,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          role: 'Admin',
          assignedClassIds: [],
          schoolId: formData.selectedSchool || null,
          createdAt: new Date().toISOString()
        });
        console.log('✅ Admin user document saved:', userCredential.user.uid);
      }

      console.log('✅ Signup complete, calling onSignupSuccess');
      onSignupSuccess();
    } catch (err) {
      if (userCredential?.user) {
        try {
          await deleteUser(userCredential.user);
        } catch (deleteErr) {
          // Best-effort cleanup; log but do not mask original error
          console.error('Failed to clean up orphaned auth user:', deleteErr);
        }
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-card">
        <h1>Create Account</h1>
        <p className="subtitle">KN Education Portal</p>
        
        <form onSubmit={handleSignup}>
          <div className="role-selection">
            <label>I am a:</label>
            <div className="role-buttons">
              <button
                type="button"
                className={`role-button ${formData.role === 'parent' ? 'active' : ''}`}
                onClick={() => setFormData({ ...formData, role: 'parent' })}
              >
                👨‍👩‍👧 Parent
              </button>
              <button
                type="button"
                className={`role-button ${formData.role === 'teacher' ? 'active' : ''}`}
                onClick={() => setFormData({ ...formData, role: 'teacher' })}
              >
                👨‍🏫 Teacher
              </button>
              <button
                type="button"
                className={`role-button ${formData.role === 'student' ? 'active' : ''}`}
                onClick={() => setFormData({ ...formData, role: 'student' })}
              >
                🎓 Student
              </button>
              <button
                type="button"
                className={`role-button ${formData.role === 'admin' ? 'active' : ''}`}
                onClick={() => setFormData({ ...formData, role: 'admin' })}
              >
                👑 Admin
              </button>
              <button
                type="button"
                className={`role-button ${formData.role === 'superAdmin' ? 'active' : ''}`}
                onClick={() => setFormData({ ...formData, role: 'superAdmin' })}
              >
                🏛️ Super Admin
              </button>
              <button
                type="button"
                className={`role-button ${formData.role === 'scanner' ? 'active' : ''}`}
                onClick={() => setFormData({ ...formData, role: 'scanner' })}
              >
                📱 Scanner
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>First Name</label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Enter your first name"
              required
            />
          </div>

          <div className="form-group">
            <label>Last Name</label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Enter your last name"
              required
            />
          </div>

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
            />
          </div>

          <div className="form-group">
            <label>Phone Number</label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder="Enter your phone number"
            />
          </div>

          {(formData.role === 'admin' || formData.role === 'teacher' || formData.role === 'scanner') && (
            <div className="form-group">
              <label>Select School</label>
              <select
                name="selectedSchool"
                value={formData.selectedSchool}
                onChange={handleChange}
                required
              >
                <option value="">Choose a school...</option>
                <optgroup label="Primary Schools">
                  {allSchools.filter(s => s.type === 'Primary').map(s => (
                    <option key={s.id || s.name} value={s.id || s.name}>{s.name}</option>
                  ))}
                </optgroup>
                <optgroup label="Secondary Schools">
                  {allSchools.filter(s => s.type === 'Secondary').map(s => (
                    <option key={s.id || s.name} value={s.id || s.name}>{s.name}</option>
                  ))}
                </optgroup>
                <optgroup label="Colleges">
                  {allSchools.filter(s => s.type === 'College').map(s => (
                    <option key={s.id || s.name} value={s.id || s.name}>{s.name}</option>
                  ))}
                </optgroup>
              </select>
            </div>
          )}

          {formData.role === 'parent' && (
            <div className="student-id-section">
              <div className="form-group">
                <label>Child's Student ID (Optional)</label>
                <input
                  type="text"
                  name="studentId"
                  value={formData.studentId}
                  onChange={handleChange}
                  placeholder="e.g., SPS-ET7HJR-2026"
                />
                <div className="info-text">
                  <span className="info-icon">ℹ</span>
                  <span>Enter your child's Student ID as shown on their ID card. You can add more children after creating your account.</span>
                </div>
              </div>
            </div>
          )}

          {formData.role === 'student' && (
            <div className="student-id-section">
              <div className="form-group">
                <label>Your Student ID (Required)</label>
                <input
                  type="text"
                  name="studentId"
                  value={formData.studentId}
                  onChange={handleChange}
                  placeholder="e.g., SPS-ET7HJR-2026"
                  required
                />
                <div className="info-text">
                  <span className="info-icon">ℹ</span>
                  <span>Enter the Student ID provided by your school. This will be used to verify your account.</span>
                </div>
              </div>
            </div>
          )}

          {formData.role === 'teacher' && (
            <div className="teacher-section">
              <div className="form-group">
                <label>Teacher Type</label>
                <div className="teacher-type-buttons">
                  <button
                    type="button"
                    className={`teacher-type-button ${formData.teacherType === 'regular' ? 'active' : ''}`}
                    onClick={() => setFormData({ ...formData, teacherType: 'regular', specialtySubjects: [] })}
                  >
                    Regular (Homeroom) Teacher
                  </button>
                  <button
                    type="button"
                    className={`teacher-type-button ${formData.teacherType === 'subject' ? 'active' : ''}`}
                    onClick={() => setFormData({ ...formData, teacherType: 'subject' })}
                  >
                    Subject Teacher
                  </button>
                </div>
              </div>

              {formData.teacherType === 'subject' && (
                <div className="form-group">
                  <label>Specialty Subjects (select one or more)</label>
                  <div className="subjects-checkboxes">
                    {["Mathematics", "English", "Science", "Biology", "Chemistry", "Social Studies", "History", "Spanish", "French", "Information Technology", "Physical Education", "Arts", "Agriculture", "Principles of Accounts (POA)", "Principles of Business (POB)", "Building Technology", "Home Economics", "Woodwork", "Reading Comprehension", "Penmanship"].map(subject => (
                      <label key={subject} className="subject-checkbox">
                        <input
                          type="checkbox"
                          value={subject}
                          checked={formData.specialtySubjects.includes(subject)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, specialtySubjects: [...formData.specialtySubjects, subject] });
                            } else {
                              setFormData({ ...formData, specialtySubjects: formData.specialtySubjects.filter(s => s !== subject) });
                            }
                          }}
                        />
                        {subject}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Create a password"
              required
            />
          </div>

          <div className="form-group">
            <label>Confirm Password</label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm your password"
              required
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" disabled={loading} className="signup-button">
            {loading ? 'Creating Account...' : `Sign Up as ${formData.role === 'parent' ? 'Parent' : formData.role === 'teacher' ? 'Teacher' : formData.role === 'admin' ? 'Admin' : formData.role === 'scanner' ? 'Scanner' : 'Super Admin'}`}
          </button>
        </form>

        <div className="login-link">
          <p>Already have an account? <a href="/login">Sign in</a></p>
        </div>
      </div>
    </div>
  );
}

export default Signup;
